import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

const fetchRents = createAsyncThunk(
  'rents/fetchRents',
  async ({ filters = {}, ordering } = {}) => {
    const params = { ...filters }
    if (ordering) params.ordering = ordering
    const response = await axiosClient.get('/companies/selected/rents', { params })
    return response.data
  }
)

const fetchRentsCount = createAsyncThunk(
  'rents/fetchRentsCount',
  async () => {
    const response = await axiosClient.get('/companies/selected/rents', { params: { page_size: 1 } })
    return response.data.count
  }
)

const fetchRentsHistory = createAsyncThunk(
  'rents/fetchRentsHistory',
  async ({ courier_id, bike_id, filters = {}, ordering } = {}) => {
    const params = { ...filters }

    if (courier_id) params.courier_id = courier_id
    if (bike_id) params.bike_id = bike_id
    if (ordering) params.ordering = ordering

    const response = await axiosClient.get('/companies/selected/rents', { params })
    return response.data
  }
)

const fetchRent = createAsyncThunk(
  'rents/fetchRent',
  async ({ id }) => {
    const response = await axiosClient.get(`/companies/selected/rents/${id}`)
    return response.data
  }
)

const fetchRentStatuses = createAsyncThunk(
  'rents/fetchRentStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.get('/companies/selected/rents/statuses')
      return data
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message)
    }
  }
)

const rentClose = createAsyncThunk(
  'rents/rentClose',
  async ({ id, client_hub_id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `companies/selected/rents/${id}/close`,
        { client_hub_id }
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const rentCancel = createAsyncThunk(
  'rents/rentCancel',
  async ({ id, client_hub_id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `companies/selected/rents/${id}/cancel`,
        { client_hub_id }
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const acceptRent = createAsyncThunk(
  'rents/acceptRent',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `companies/selected/rents/${id}/accept-rent`
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

// Выдача байка по аренде, которую завёл оператор: курьер пришёл и взял байк,
// приложением не пользуясь. Приёмку, счёт и выдачу оператор проходит разом
const handOverRent = createAsyncThunk(
  'rents/handOverRent',
  async ({ id, markPaid = false, paymentMethod = '' }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        `companies/selected/rents/${id}/hand-over`,
        { mark_paid: markPaid, payment_method: paymentMethod }
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message)
    }
  }
)

const fetchAvailableBikes = createAsyncThunk(
  'rents/fetchAvailableBikes',
  async () => {
    const response = await axiosClient.get(`/companies/selected/rents/available-bikes`)
    return response.data.data
  }
)

const fetchAvailableCouriers = createAsyncThunk(
  'rents/fetchAvailableCouriers',
  async () => {
    const response = await axiosClient.get(`/companies/selected/rents/available-couriers`)
    return response.data.data
  }
)

const markFirstPaymentAsPaid = createAsyncThunk(
  'rents/markFirstPaymentAsPaid',
  async (rent_id, { dispatch, rejectWithValue, getState }) => {
    try {
      const resp = await axiosClient.get('/companies/selected/payments', {
        params: { rent_id },
      })
      const payments = resp.data.results || []
      if (!payments.length) {
        return rejectWithValue({ message: 'no_payments' })
      }

      const firstPayment = payments[0]
      await axiosClient.post(
        `/companies/selected/payments/${firstPayment.id}/set-as-paid`,
        {
          status: '200.1',
          payment_type: 'CARD',
        }
      )

      const state = getState()
      const listItems = state.rents.listItems || {}
      const {
        ordering,
        filters = {},
        page,
        page_size,
      } = listItems

      const apiFilters = {
        ...filters,
        page,
        page_size,
      }

      dispatch(fetchRents({ filters: apiFilters, ordering }))

      return firstPayment.id
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

// Получить список документов аренды
const fetchRentDocuments = createAsyncThunk(
  'rents/fetchRentDocuments',
  async (rent_id, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.get(
        `/companies/selected/rents/${rent_id}/documents`
      )
      return { rent_id, data }
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message)
    }
  }
)

// Сгенерировать документ (doc_type: RENTAL_TERMS | BIKE_ACCEPTANCE | BIKE_HANDOVER)
const generateRentDocument = createAsyncThunk(
  'rents/generateRentDocument',
  async (
    { rent_id, doc_type, custom_data = null, generate_pdf = true },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axiosClient.post(
        `/companies/selected/rents/${rent_id}/generate-contract`,
        {
          doc_type,
          custom_data,
          generate_pdf,
        }
      )

      const list = Array.isArray(data) ? data : [data]
      const item = list[list.length - 1] || null

      return { rent_id, item }
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message)
    }
  }
)

const uploadRentDocument = createAsyncThunk(
  'rents/uploadRentDocument',
  async ({ rent_id, doc_type, file }, { rejectWithValue }) => {
    try {
      const form = new FormData()
      form.append('doc_type', doc_type)
      form.append('pdf_file', file)
      const { data } = await axiosClient.post(
        `/companies/selected/rents/${rent_id}/upload-contract`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return { rent_id, item: data }
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message)
    }
  }
)

const signRentDocument = createAsyncThunk(
  'rents/signRentDocument',
  async ({ rent_id, doc_type }, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.post(
        `/companies/selected/rents/${rent_id}/sign-contract`,
        { doc_type }
      )
      return { rent_id, item: data }
    } catch (e) {
      return rejectWithValue(e.response?.data || e.message)
    }
  }
)

const initialState = {
  rentsCount: null,
  listItems: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0,
    ordering: '-created_at',
    filters: {},
  },

  rentsHistoryListItems: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0,
    ordering: '-created_at',
    filters: {},
  },
  rentsHistoryLoading: false,
  rentsHistoryError: null,

  rentDocuments: {
    byRentId: {},
    loading: false,
    error: null,
    loadingMap: {},
  },
  rents: [],
  rent: null,
  availableBikes: [],
  availableCouriers: [],
  loadingPayments: [],
  statuses: {
    items: [],
    loading: false,
    error: null,
  },
}

export const slice = createSlice({
  name: 'rents',
  initialState,
  reducers: {
    setListItems(state, { payload }) {
      state.listItems = {
        ...state.listItems,
        ...payload,
      }
      state.rents = state.listItems.items
    },
    resetListItems(state) {
      state.listItems = {
        items: [],
        page: 1,
        page_size: 10,
        count: 0,
        ordering: '-created_at',
        filters: {},
      }
    },

    setRentsHistoryListItems(state, { payload }) {
      state.rentsHistoryListItems = {
        ...state.rentsHistoryListItems,
        ...payload,
      }
    },
    resetRentsHistoryListItems(state) {
      state.rentsHistoryListItems = {
        items: [],
        page: 1,
        page_size: 10,
        count: 0,
        ordering: '-created_at',
        filters: {},
      }
      state.rentsHistoryLoading = false
      state.rentsHistoryError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRentsCount.fulfilled, (state, { payload }) => {
        state.rentsCount = payload
      })
      .addCase(fetchRents.fulfilled, (state, { payload }) => {
        if (!payload) {
          state.listItems.items = []
          state.listItems.count = 0
          return
        }
        const { count = 0, results = null } = payload
        const items = Array.isArray(results)
          ? results
          : Array.isArray(payload)
          ? payload
          : payload.results || []

        state.listItems = {
          ...state.listItems,
          items,
          count,
        }
        state.rents = items
      })

      .addCase(fetchRentsHistory.pending, (state) => {
        state.rentsHistoryLoading = true
        state.rentsHistoryError = null
      })
      .addCase(fetchRentsHistory.fulfilled, (state, { payload }) => {
        state.rentsHistoryLoading = false

        if (!payload) {
          state.rentsHistoryListItems.items = []
          state.rentsHistoryListItems.count = 0
          return
        }

        const { count = 0, results = null } = payload

        const items = Array.isArray(results)
          ? results
          : Array.isArray(payload)
          ? payload
          : payload.results || []

        state.rentsHistoryListItems = {
          ...state.rentsHistoryListItems,
          items,
          count,
        }
      })
      .addCase(fetchRentsHistory.rejected, (state, { payload, error }) => {
        state.rentsHistoryLoading = false
        state.rentsHistoryError =
          typeof payload === 'string'
            ? payload
            : payload?.detail ||
              payload?.message ||
              error?.message ||
              'Ошибка загрузки истории аренд'
      })

      // ---- SINGLE
      .addCase(fetchRent.fulfilled, (state, { payload }) => {
        state.rent = payload
      })
      .addCase(fetchAvailableBikes.fulfilled, (state, { payload }) => {
        state.availableBikes = payload
      })
      .addCase(fetchAvailableCouriers.fulfilled, (state, { payload }) => {
        state.availableCouriers = payload
      })
      .addCase(rentClose.fulfilled, (state, { payload }) => {
        state.rent = payload
      })
      .addCase(rentCancel.fulfilled, (state, { payload }) => {
        state.rent = payload
      })
      .addCase(acceptRent.fulfilled, (state, { payload }) => {
        state.rent = payload
      })
      .addCase(markFirstPaymentAsPaid.pending, (state, { meta }) => {
        const rentId = meta.arg
        state.loadingPayments.push(rentId)
      })
      .addCase(markFirstPaymentAsPaid.fulfilled, (state, { payload }) => {
        state.loadingPayments = state.loadingPayments.filter(
          (id) => id !== payload
        )
      })
      .addCase(markFirstPaymentAsPaid.rejected, (state, { meta }) => {
        const rentId = meta.arg
        state.loadingPayments = state.loadingPayments.filter(
          (id) => id !== rentId
        )
      })
      // ---- DOCUMENTS: LIST
      .addCase(fetchRentDocuments.pending, (state) => {
        state.rentDocuments.loading = true
        state.rentDocuments.error = null
      })
      .addCase(fetchRentDocuments.fulfilled, (state, { payload }) => {
        state.rentDocuments.loading = false
        state.rentDocuments.byRentId[payload.rent_id] = payload.data || []
      })
      .addCase(fetchRentDocuments.rejected, (state, { payload }) => {
        state.rentDocuments.loading = false
        state.rentDocuments.error =
          typeof payload === 'string'
            ? payload
            : payload?.detail || 'Ошибка загрузки документов'
      })
      // ---- DOCUMENTS: GENERATE
      .addCase(generateRentDocument.pending, (state, { meta }) => {
        const { doc_type } = meta.arg || {}
        state.rentDocuments.loadingMap[doc_type] = true
      })
      .addCase(generateRentDocument.fulfilled, (state, { payload }) => {
        const { rent_id, item } = payload
        const list = state.rentDocuments.byRentId[rent_id] || []
        if (item) {
          const idx = list.findIndex((d) => d.doc_type === item.doc_type)
          if (idx >= 0) list[idx] = item
          else list.push(item)
          state.rentDocuments.byRentId[rent_id] = [...list]
          state.rentDocuments.loadingMap[item.doc_type] = false
        }
      })
      .addCase(generateRentDocument.rejected, (state, { meta }) => {
        const { doc_type } = meta.arg || {}
        state.rentDocuments.loadingMap[doc_type] = false
      })
      // ---- DOCUMENTS: UPLOAD
      .addCase(uploadRentDocument.pending, (state, { meta }) => {
        const { doc_type } = meta.arg || {}
        state.rentDocuments.loadingMap[doc_type] = true
      })
      .addCase(uploadRentDocument.fulfilled, (state, { payload }) => {
        const { rent_id, item } = payload
        const list = state.rentDocuments.byRentId[rent_id] || []
        const idx = list.findIndex((d) => d.doc_type === item.doc_type)
        if (idx >= 0) list[idx] = item
        else list.push(item)
        state.rentDocuments.byRentId[rent_id] = [...list]
        state.rentDocuments.loadingMap[item.doc_type] = false
      })
      .addCase(uploadRentDocument.rejected, (state, { meta }) => {
        const { doc_type } = meta.arg || {}
        state.rentDocuments.loadingMap[doc_type] = false
      })
      // ---- DOCUMENTS: SIGN (electronic, no file - see SignRentDocumentView)
      .addCase(signRentDocument.pending, (state, { meta }) => {
        const { doc_type } = meta.arg || {}
        state.rentDocuments.loadingMap[doc_type] = true
      })
      .addCase(signRentDocument.fulfilled, (state, { payload }) => {
        const { rent_id, item } = payload
        const list = state.rentDocuments.byRentId[rent_id] || []
        const idx = list.findIndex((d) => d.doc_type === item.doc_type)
        if (idx >= 0) list[idx] = item
        else list.push(item)
        state.rentDocuments.byRentId[rent_id] = [...list]
        state.rentDocuments.loadingMap[item.doc_type] = false
      })
      .addCase(signRentDocument.rejected, (state, { meta }) => {
        const { doc_type } = meta.arg || {}
        state.rentDocuments.loadingMap[doc_type] = false
      })

      .addCase(fetchRentStatuses.pending, (state) => {
        state.statuses.loading = true
        state.statuses.error = null
      })
      .addCase(fetchRentStatuses.fulfilled, (state, { payload }) => {
        state.statuses.loading = false
        state.statuses.items = Array.isArray(payload) ? payload : []
      })
      .addCase(fetchRentStatuses.rejected, (state, { payload }) => {
        state.statuses.loading = false
        state.statuses.error =
          typeof payload === 'string'
            ? payload
            : payload?.detail || 'Ошибка загрузки статусов аренды'
      })
  },
})

export const {
  setListItems,
  resetListItems,
  setRentsHistoryListItems,
  resetRentsHistoryListItems,
} = slice.actions

export {
  fetchRents,
  fetchRentsCount,
  fetchRentsHistory,
  fetchRent,
  fetchRentStatuses,
  rentClose,
  rentCancel,
  fetchAvailableBikes,
  fetchAvailableCouriers,
  markFirstPaymentAsPaid,
  fetchRentDocuments,
  generateRentDocument,
  uploadRentDocument,
  signRentDocument,
  acceptRent,
  handOverRent,
}

export default slice.reducer
